import { useEffect, useState } from "react"
import { API_ROUTES, NOTIF_PERMISSION_NEVER_KEY } from "../../../core/Constants"
import { getOwner, setOwnerUserId, setUserId, signature, useDatabase, useHub } from "../../../core/Global"
import Avatar from "../../base/Avatar"
import Icon from "../../base/Icon"
import Indicator from "../../base/Indicator"
import { toastError } from "../../base/Toast"

export default function HeaderConnectedAccounts() {

    const hub = useHub()
    const db = useDatabase()
    const owner = getOwner()

    const [switchingId, switchingIdSet] = useState<string>()

    useEffect(() => {
        window.KTMenuCreate()
    }, [])

    const accounts = owner?.connectedAccounts ?? []

    // Switching here signs in as a different account entirely, so it has to leave as little of the
    // previous one behind as the workspace switcher does: the cached database is this account's
    // jobs, chats and base data, and carrying it into the next one shows their data under someone
    // else's name. Cleared before the new session is written, and awaited so the reload cannot
    // race it -- a reload with the old cache still in place is exactly what it is meant to avoid.
    const switchTo = (userId?: string) => {
        if (!userId || !owner?.id || switchingId) return

        switchingIdSet(userId)

        hub.Post(API_ROUTES.SwitchAccount, {
            currentUserId: owner.id,
            switchUserId: userId
        }, res => {
            if (!res.success) {
                toastError(res.message)
                switchingIdSet(undefined)
                return
            }

            // Whoever is signed in next has to be asked about notifications again, the same way
            // signing out asks again.
            localStorage.removeItem(NOTIF_PERMISSION_NEVER_KEY)

            db.deleteDatabase().then(() => {
                signature(res.data.session.signature)
                setUserId(res.data.user.id)
                setOwnerUserId(res.data.user.id)
                document.location.reload()
            })
        })
    }

    return (
        <div className="me-3">

            <div className="d-flex align-items-center cursor-pointer"
                data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end">
                {
                    switchingId ? <Indicator text="Switching account..." className="fs-7" />
                        :
                        <>
                            <Icon icon="briefcase" type="solid" className="text-gray-600 me-2" size={16} />
                            <span className="fw-bold fs-7 text-gray-800">
                                {owner?.companyName ?? "Select Workspace"}
                            </span>
                            <Icon icon="chevron-down" className="text-gray-500 ms-2" size={10} />
                        </>
                }
            </div>

            <div className="menu menu-sub menu-sub-dropdown menu-column menu-rounded fw-bold py-3 w-300px"
                data-kt-menu="true">

                <div className="menu-item px-3 pb-2">
                    <div className="text-muted text-uppercase fs-9 ls-1">Switch account</div>
                </div>

                <div className="separator mb-2"></div>

                <div className="menu-item px-3 py-2 d-flex align-items-center bg-light-primary rounded mx-3 mb-2">
                    <Avatar avatarUrl={owner?.avatarUrl} fullName={owner?.fullName} width={34} className="me-3" />
                    <div className="flex-grow-1 min-w-0">
                        <div className="fw-bold fs-7 text-gray-900 text-truncate">
                            {owner?.companyName ?? owner?.fullName}
                        </div>
                        <div className="text-muted fw-normal fs-8 text-truncate">{owner?.fullName}</div>
                    </div>
                    <Icon icon="circle-check" type="solid" className="text-primary ms-2" size={15} />
                </div>

                {
                    accounts.map(a =>
                        <div key={a.userId}
                            className="menu-item px-3 py-2 d-flex align-items-center cursor-pointer rounded mx-3 bg-hover-light"
                            onClick={() => switchTo(a.userId)}>
                            <Avatar avatarUrl={a.avatarUrl} fullName={a.fullName} width={34} className="me-3" />
                            <div className="flex-grow-1 min-w-0">
                                <div className="fw-bold fs-7 text-gray-900 text-truncate">
                                    {a.companyName && a.companyName !== "" ? a.companyName : a.fullName}
                                </div>
                                <div className="text-muted fw-normal fs-8 text-truncate">{a.fullName}</div>
                            </div>
                            {
                                switchingId == a.userId
                                    ? <Icon icon="spinner-third" spin className="text-primary ms-2" size={14} />
                                    : <Icon icon="arrow-right-arrow-left" className="text-gray-400 ms-2" size={13} />
                            }
                        </div>
                    )
                }

            </div>
        </div>
    )
}
